import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import socket, { sessionId } from '../socket';
import { resetGame, setRematchMine } from '../store/slices/lobbySlice';
import { toggleDirection, placeShipAt, selectShip, resetBS, setPhase } from '../store/slices/battleshipSlice';
import { getShipCells, canPlaceShip } from '../placement';
import { playHit, playMiss } from '../sounds';
import GameOverDialog from './GameOverDialog';

// ── Helpers ──
const BOARD_SIZE = 10;
const COLS = 'ABCDEFGHIJ'.split('');

function emptyBoard() {
  return Array.from({ length: BOARD_SIZE }, () => Array(BOARD_SIZE).fill(0));
}

function cellClass(val, isOwn) {
  if (val === 3) return 'cell-hit';
  if (val === 2) return 'cell-miss';
  if (val === 1 && isOwn) return 'cell-ship';
  return '';
}

// ── Component ──
export default function Battleship() {
  const dispatch = useDispatch();
  const {
    phase, myBoard, opponentBoard, currentTurn, winner,
    shipsToPlace, placedShips, placementBoard, selectedShipIdx, placingDirection,
  } = useSelector((s) => s.battleship);
  const room = useSelector((s) => s.lobby.currentRoom);
  const opponentLeft = useSelector((s) => s.lobby.opponentLeft);
  const opponentDisconnected = useSelector((s) => s.lobby.opponentDisconnected);
  const rematchMine = useSelector((s) => s.lobby.rematchMine);
  const rematchOpponent = useSelector((s) => s.lobby.rematchOpponent);
  const myId = sessionId;

  const [hoverCells, setHoverCells] = useState([]);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [pendingShot, setPendingShot] = useState(null); // {x,y} sent, awaiting server
  const lastShotRef = useRef(null);
  const pendingTimerRef = useRef(null);
  const pressRef = useRef(null); // where the pointer went down, for tap detection

  // Tap = press and release on the same cell without dragging. We fire on
  // pointerup instead of click because a few pixels of finger/mouse movement
  // makes Android WebView treat the tap as a scroll and swallow the click —
  // the cause of "I have to click twice" — while pointer events always fire.
  const pressCell = (e, x, y) => {
    pressRef.current = { x, y, cx: e.clientX, cy: e.clientY };
  };
  const releaseCell = (e, x, y, action) => {
    const p = pressRef.current;
    pressRef.current = null;
    if (!p || p.x !== x || p.y !== y) return;
    if (Math.abs(e.clientX - p.cx) > 12 || Math.abs(e.clientY - p.cy) > 12) return; // drag/scroll
    action(x, y);
  };

  // Clear the in-flight marker as soon as any server update lands (bs:update
  // and session restore both replace the opponentBoard reference)
  useEffect(() => {
    setPendingShot(null);
    clearTimeout(pendingTimerRef.current);
  }, [opponentBoard]);

  // Clear any stale hover preview when a new placing phase begins (rematch)
  useEffect(() => {
    if (phase === 'placing') setHoverCells([]);
  }, [phase]);

  // ── Play sound on every new shot result ──
  const lastShot = useSelector((s) => s.battleship.lastShot);
  useEffect(() => {
    if (!lastShot) return;
    // Compare with previous to avoid replaying on re-render
    const key = `${lastShot.x}-${lastShot.y}-${lastShot.result}`;
    if (lastShotRef.current === key) return;
    lastShotRef.current = key;
    if (lastShot.result === 'hit') playHit();
    else if (lastShot.result === 'miss') playMiss();
  }, [lastShot]);

  const currentShip = shipsToPlace[selectedShipIdx];
  const allPlaced = shipsToPlace.every((s) => s.placed);
  const isMyTurn = currentTurn === myId;

  // ── Placing Ships ──
  // Placement decisions live in the reducer (placeShipAt) so they always run
  // against the latest state; the component only previews and dispatches.
  const handlePlaceHover = (x, y) => {
    if (!currentShip || currentShip.placed) return;
    const cells = getShipCells(x, y, currentShip.size, placingDirection);
    setHoverCells(cells && canPlaceShip(placementBoard, cells) ? cells : []);
  };

  const handlePlaceClick = (x, y) => {
    dispatch(placeShipAt({ x, y }));
    setHoverCells([]);
  };

  const submitShips = () => {
    if (!socket.connected) return;
    socket.emit('bs:placeShips', { roomId: room.id, ships: placedShips });
    dispatch(setPhase('waiting'));
  };

  // ── Shooting ──
  const handleFire = (x, y) => {
    if (!isMyTurn || phase !== 'playing' || winner || !socket.connected) return;
    if (pendingShot) return; // one shot in flight at a time
    if (opponentBoard && opponentBoard[y][x] !== 0) return; // already shot

    // Show feedback immediately — the result arrives with the server update.
    setPendingShot({ x, y });
    // Safety valve: unlock if the update never arrives (e.g. lost mid-reconnect)
    clearTimeout(pendingTimerRef.current);
    pendingTimerRef.current = setTimeout(() => setPendingShot(null), 4000);

    socket.emit('bs:fire', { roomId: room.id, x, y });
  };

  // ── Leave ──
  const leave = () => {
    socket.emit('game:leave', { roomId: room.id });
    dispatch(resetBS());
    dispatch(resetGame());
  };

  const requestRematch = () => {
    if (!socket.connected) return;
    socket.emit('rematch:request', { roomId: room.id });
    dispatch(setRematchMine(true));
  };

  const handleLeaveClick = () => {
    // If game is over or opponent left, leave immediately without confirmation
    if (winner || opponentLeft) {
      leave();
    } else {
      setShowLeaveConfirm(true);
    }
  };

  const getPlayerName = (id) => {
    if (!room) return '';
    const idx = room.players.indexOf(id);
    return room.playerNames?.[idx] || id;
  };

  // ── Leave confirmation modal ──
  const leaveConfirmModal = showLeaveConfirm ? (
    <div className="modal-overlay">
      <div className="modal">
        <h3>🚪 Leave Game</h3>
        <p>Are you sure? The game is still in progress. <strong>Leaving will count as a forfeit</strong> and your opponent will be notified.</p>
        <div className="modal-actions">
          <button className="btn btn-danger" onClick={() => { setShowLeaveConfirm(false); leave(); }}>Yes, Leave</button>
          <button className="btn btn-primary" onClick={() => setShowLeaveConfirm(false)}>Cancel</button>
        </div>
      </div>
    </div>
  ) : null;

  // ── Opponent left overlay ──
  const opponentLeftOverlay = opponentLeft ? (
    <div className="modal-overlay">
      <div className="modal">
        <h3>🚪 Opponent Left</h3>
        <p><strong>{getPlayerName(room?.players?.find(p => p !== myId)) || 'Your opponent'}</strong> has left the game.</p>
        <div className="modal-actions">
          <button className="btn btn-primary" onClick={leave}>Back to Lobby</button>
        </div>
      </div>
    </div>
  ) : null;

  // ── Opponent disconnected overlay ──
  const opponentDisconnectedOverlay = !opponentLeft && opponentDisconnected ? (
    <div className="modal-overlay">
      <div className="modal">
        <h3>⚠️ Opponent Disconnected</h3>
        <p><strong>{getPlayerName(room?.players?.find(p => p !== myId)) || 'Your opponent'}</strong> lost connection. Waiting for them to reconnect...</p>
        <div className="spinner" style={{ margin: '10px auto' }} />
        <div className="modal-actions">
          <button className="btn btn-danger" onClick={leave}>Leave Game</button>
        </div>
      </div>
    </div>
  ) : null;

  // ── Game over dialog (opponent-left / disconnected overlays win priority) ──
  const gameOverDialog = winner && !opponentLeft && !opponentDisconnected && !showLeaveConfirm ? (
    <GameOverDialog
      result={winner === myId ? 'win' : 'lose'}
      rematchMine={rematchMine}
      rematchOpponent={rematchOpponent}
      onRestart={requestRematch}
      onLeave={leave}
    />
  ) : null;

  // ── Status Text ──
  let statusText;
  if (opponentLeft) {
    statusText = 'Opponent left!';
  } else if (winner) {
    statusText = winner === myId ? '🎉 You win!' : `${getPlayerName(winner)} wins!`;
  } else if (phase === 'placing') {
    statusText = 'Place your ships on the board';
  } else if (phase === 'waiting') {
    statusText = 'Waiting for opponent to place ships...';
  } else if (isMyTurn) {
    statusText = '🎯 Your turn — fire at the enemy board!';
  } else {
    statusText = `Waiting for ${getPlayerName(currentTurn)}...`;
  }

  // ── Render Placing Phase ──
  if (phase === 'placing') {
    return (
      <div className="game-container">
        {leaveConfirmModal}
        {opponentLeftOverlay}
        {opponentDisconnectedOverlay}
        <div className="game-top-bar">
          <h2>🚢 Battleship</h2>
          <button className="btn btn-small btn-leave" onClick={handleLeaveClick}>Leave Game</button>
        </div>
        <p className="status">{statusText}</p>

        <div className="bs-placing-layout">
          <div>
            <h3>Your Board</h3>
            <div className="bs-board">
              {/* Column headers */}
              <div className="bs-cell bs-header" />
              {COLS.map((c) => <div key={c} className="bs-cell bs-header">{c}</div>)}

              {placementBoard.map((row, y) => (
                <React.Fragment key={y}>
                  <div className="bs-cell bs-header">{y + 1}</div>
                  {row.map((cell, x) => {
                    const isHover = hoverCells.some((c) => c.x === x && c.y === y);
                    return (
                      <div
                        key={`${x}-${y}`}
                        className={`bs-cell ${cellClass(cell, true)} ${isHover ? 'cell-hover' : ''}`}
                        onMouseEnter={() => handlePlaceHover(x, y)}
                        onMouseLeave={() => setHoverCells([])}
                        onPointerDown={(e) => pressCell(e, x, y)}
                        onPointerUp={(e) => releaseCell(e, x, y, handlePlaceClick)}
                      />
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="ship-selector">
            <h3>Ships</h3>
            <button className="btn btn-small mb" onClick={() => dispatch(toggleDirection())}>
              Rotate ({placingDirection === 'horizontal' ? '→' : '↓'})
            </button>
            {shipsToPlace.map((ship, idx) => (
              <div
                key={idx}
                className={`ship-item ${ship.placed ? 'placed' : ''} ${
                  idx === selectedShipIdx ? 'selected' : ''
                }`}
                onClick={() => dispatch(selectShip(idx))}
              >
                {ship.name} ({ship.size}) {ship.placed ? '✓' : ''}
              </div>
            ))}
            {allPlaced && (
              <button className="btn btn-primary mt" onClick={submitShips}>
                Ready!
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Render Waiting Phase ──
  if (phase === 'waiting') {
    return (
      <div className="game-container">
        {leaveConfirmModal}
        {opponentLeftOverlay}
        {opponentDisconnectedOverlay}
        <div className="game-top-bar">
          <h2>🚢 Battleship</h2>
          <button className="btn btn-small btn-leave" onClick={handleLeaveClick}>Leave Game</button>
        </div>
        <p className="status">{statusText}</p>
        <div className="spinner" />
      </div>
    );
  }

  // ── Render Playing / Finished Phase ──
  const displayMyBoard = myBoard || emptyBoard();
  const displayOpBoard = opponentBoard || emptyBoard();

  const TOTAL_HP = 16; // 4+3+3+2+2+1+1
  const myHitsReceived = displayMyBoard.flat().filter(c => c === 3).length;
  const opHitsReceived = displayOpBoard.flat().filter(c => c === 3).length;
  const myHp = TOTAL_HP - myHitsReceived;
  const opHp = TOTAL_HP - opHitsReceived;

  return (
    <div className="game-container">
      {leaveConfirmModal}
      {opponentLeftOverlay}
      {opponentDisconnectedOverlay}
      {gameOverDialog}
      <div className="game-top-bar">
        <h2>🚢 Battleship</h2>
        <button className="btn btn-small btn-leave" onClick={handleLeaveClick}>
          {winner || opponentLeft ? 'Back to Lobby' : 'Leave Game'}
        </button>
      </div>
      <p className="game-vs">
        {room?.playerNames?.[0]} vs {room?.playerNames?.[1]}
      </p>

      {/* Health Bars */}
      <div className="health-bars">
        <div className="hb-side hb-left">
          <span className="hb-name">{getPlayerName(myId)}</span>
          <div className="hb-track">
            <div
              className={`hb-fill ${myHp <= 4 ? 'hb-critical' : myHp <= 8 ? 'hb-warning' : 'hb-healthy'}`}
              style={{ width: `${(myHp / TOTAL_HP) * 100}%` }}
            />
          </div>
          <span className="hb-count">{myHp}/{TOTAL_HP}</span>
        </div>
        <span className="hb-vs">⚔️</span>
        <div className="hb-side hb-right">
          <span className="hb-name">{getPlayerName(room?.players?.find(p => p !== myId))}</span>
          <div className="hb-track">
            <div
              className={`hb-fill ${opHp <= 4 ? 'hb-critical' : opHp <= 8 ? 'hb-warning' : 'hb-healthy'}`}
              style={{ width: `${(opHp / TOTAL_HP) * 100}%` }}
            />
          </div>
          <span className="hb-count">{opHp}/{TOTAL_HP}</span>
        </div>
      </div>

      <p className={`status ${winner === myId ? 'status-win' : ''}`}>{statusText}</p>

      <div className="bs-game-layout">
        {/* My Board */}
        <div>
          <h3>Your Board</h3>
          <div className="bs-board">
            <div className="bs-cell bs-header" />
            {COLS.map((c) => <div key={c} className="bs-cell bs-header">{c}</div>)}
            {displayMyBoard.map((row, y) => (
              <React.Fragment key={y}>
                <div className="bs-cell bs-header">{y + 1}</div>
                {row.map((cell, x) => (
                  <div key={`${x}-${y}`} className={`bs-cell ${cellClass(cell, true)}`} />
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Opponent Board */}
        <div>
          <h3>Enemy Board</h3>
          <div className="bs-board">
            <div className="bs-cell bs-header" />
            {COLS.map((c) => <div key={c} className="bs-cell bs-header">{c}</div>)}
            {displayOpBoard.map((row, y) => (
              <React.Fragment key={y}>
                <div className="bs-cell bs-header">{y + 1}</div>
                {row.map((cell, x) => {
                  const isPending = pendingShot?.x === x && pendingShot?.y === y;
                  return (
                    <div
                      key={`${x}-${y}`}
                      className={`bs-cell ${cellClass(cell, false)} ${
                        isMyTurn && cell === 0 && !winner && !pendingShot ? 'cell-target' : ''
                      } ${isPending ? 'cell-pending' : ''}`}
                      onPointerDown={(e) => pressCell(e, x, y)}
                      onPointerUp={(e) => releaseCell(e, x, y, handleFire)}
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}
